import { useCallback, useRef } from "react";
import { tonesForImage } from "../utils/imageTones";

/**
 * useImagePersonalisation — Phase 1 frontend hook
 * --------------------------------------------------------------------
 * Tracks user image interactions (view, select, skip) and exposes a
 * `rank()` helper to re-order an image array using the user's
 * emotional-tone profile.
 *
 * Endpoints:
 *   POST /api/image-personalisation/track
 *   POST /api/image-personalisation/track/batch
 *   GET  /api/image-personalisation/profile
 *   POST /api/image-personalisation/rank
 *
 * Fire-and-forget by design — never blocks the UI on a tracking call.
 */
export default function useImagePersonalisation() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const queueRef = useRef([]);

  const authHeaders = useCallback(() => {
    try {
      const t = localStorage.getItem("reclaim_token");
      return t ? { Authorization: `Bearer ${t}` } : {};
    } catch {
      return {};
    }
  }, []);

  const track = useCallback(
    (image, action, area, extra = {}) => {
      if (!image) return;
      const tones = tonesForImage(image, image?.category || extra.category);
      const payload = {
        image_id: image.id || image.url,
        image_url: image.url,
        area: area || "vision_board",
        action,
        tones,
        duration_ms: extra.duration_ms,
        metadata: extra.metadata || null,
      };
      // Fire-and-forget
      try {
        fetch(`${API}/api/image-personalisation/track`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {});
      } catch {}
    },
    [API, authHeaders]
  );

  const queueView = useCallback(
    (image, area, durationMs) => {
      if (!image) return;
      queueRef.current.push({
        image_id: image.id || image.url,
        image_url: image.url,
        area: area || "vision_board",
        action: "view",
        tones: tonesForImage(image, image?.category),
        duration_ms: durationMs,
      });
      // Auto-flush at 25
      if (queueRef.current.length >= 25) flushQueue();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    []
  );

  const flushQueue = useCallback(() => {
    if (!queueRef.current.length) return;
    const events = queueRef.current.splice(0, queueRef.current.length);
    try {
      fetch(`${API}/api/image-personalisation/track/batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ events }),
        keepalive: true,
      }).catch(() => {});
    } catch {}
  }, [API, authHeaders]);

  const getProfile = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/image-personalisation/profile`, {
        headers: authHeaders(),
      });
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }, [API, authHeaders]);

  /**
   * Re-rank a list of images using the user's profile.
   * Each image must include either explicit `tones[]` or `category` to
   * derive tones from.
   */
  const rank = useCallback(
    async (images, fallbackCategory) => {
      if (!images || !images.length) return images;
      const enriched = images.map((img) => ({
        ...img,
        id: img.id || img.url,
        tones: tonesForImage(img, img.category || fallbackCategory),
      }));
      try {
        const res = await fetch(`${API}/api/image-personalisation/rank`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({ images: enriched }),
        });
        if (!res.ok) return images;
        const data = await res.json();
        if (!data.personalised) return images;
        // Map ranked id order back to the original image objects
        const byId = new Map(images.map((i) => [i.id || i.url, i]));
        return data.images.map((r) => byId.get(r.id) || r);
      } catch {
        return images;
      }
    },
    [API, authHeaders]
  );

  return { track, queueView, flushQueue, getProfile, rank };
}
